/**
 * The brand-media picker, minus its modal. Generate, upload, browse the
 * library, and (in manage mode) preview and delete.
 *
 * It is a body rather than a whole modal because a slide slot now offers two
 * ways to fill itself — this library, or something the platform already holds
 * (a reference, a trend video) — and the two live as tabs of one modal. The
 * split keeps that from becoming a second copy of this.
 *
 * The active tab is CONTROLLED by the host: the modal's header line describes
 * the tab you are on, and a body that owned the tab privately could not tell it.
 */
import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ImagePlus, Sparkles, Trash2 } from "lucide-react";
import { Image } from "@/components/Image";
import { cn } from "@/utils/helpers";
import type {
  BrandMediaAspectRatio,
  BrandMediaGenerateInput,
  BrandMediaItem,
  BrandMediaResolution,
} from "@/types/brandMedia.type";
import {
  BRAND_MEDIA_ACCEPT,
  GENERATION_MAX_EDGE,
  GENERATION_MAX_RATIO,
  GENERATION_MIN_EDGE,
  generationPresetSize,
  snapGenerationEdge,
  useBrandMedia,
  useBrandMediaMutations,
} from "../../api/useBrandMedia";
import {
  KitButton,
  KitInput,
  KitSegmentedControl,
  KitSelect,
  KitSelectItem,
  KitTextarea,
} from "../kit";

export type MediaPickerTab = "library" | "upload" | "generate";

const STRIP_COUNT = 5;

type ShapeChoice = BrandMediaAspectRatio | "custom";

const SHAPE_OPTIONS: { label: string; value: ShapeChoice }[] = [
  { label: "Square — 1:1", value: "1:1" },
  { label: "Landscape — 3:2", value: "3:2" },
  { label: "Portrait — 2:3", value: "2:3" },
  { label: "Landscape — 4:3", value: "4:3" },
  { label: "Portrait — 3:4", value: "3:4" },
  { label: "Wide — 16:9", value: "16:9" },
  { label: "Vertical — 9:16", value: "9:16" },
  { label: "Custom size", value: "custom" },
];

const RESOLUTION_OPTIONS: { label: string; value: BrandMediaResolution }[] = [
  { label: "1K", value: "1K" },
  { label: "2K", value: "2K" },
];

/**
 * Where the picker's Upload and Generate tabs create an image. Omitted = the
 * brand library. A workshop project passes its own, so nothing made there lands
 * in the library.
 */
export interface MediaCreateTarget {
  upload: { mutateAsync: (file: File) => Promise<BrandMediaItem>; isPending: boolean };
  generate: {
    mutateAsync: (input: BrandMediaGenerateInput) => Promise<BrandMediaItem>;
    isPending: boolean;
  };
  /** The Upload tab's header line. */
  uploadDescription: string;
  /** Where Generate saves, said under its button. Defaults to the brand library. */
  generateDescription?: string;
}

/** Where a picked item came from: the library grid, or just made through the picker. */
export type MediaPickOrigin = "library" | "created";

/** The modal header's second line, which follows whichever tab is open. */
export function mediaPickerDescription(
  tab: MediaPickerTab,
  createTarget?: MediaCreateTarget,
): string {
  if (tab === "upload") {
    return createTarget?.uploadDescription ?? "Upload an image into the brand library.";
  }
  if (tab === "library") return "Pick from the brand's library.";
  return "Write a prompt to generate, or pick a picture you already have.";
}

export interface MediaPickerBodyProps {
  brandId: string;
  /** Gates the library query, so a closed modal fetches nothing. */
  isOpen: boolean;
  /** A Guest (a brand's client): never read the brand library. */
  hideLibrary?: boolean;
  tab: MediaPickerTab;
  onTabChange: (tab: MediaPickerTab) => void;
  /**
   * "select" hands one item back and closes (a slide slot asked for it).
   * "manage" is the library itself: preview and delete, nothing to hand back.
   */
  mode: "select" | "manage";
  /** Seeds the generate form so slide art comes out shaped like its slot. */
  /** 4:5 opens as the equivalent exact custom size; the image service has no 4:5 preset. */
  defaultAspect?: BrandMediaAspectRatio | "4:5";
  /**
   * Seeds the generate form's prompt (a strategy slot arrives with one written
   * for it), so the user can generate instantly or edit first.
   */
  initialPrompt?: string;
  /** Opens straight onto one item's detail, for a caller that already listed them. */
  initialPreviewId?: string;
  onSelect?: (item: BrandMediaItem, origin: MediaPickOrigin) => void;
  /** Where Upload and Generate create the image; the brand library when omitted. */
  createTarget?: MediaCreateTarget;
  /** Dismisses the host modal. It is responsible for resetting the tab. */
  onClose: () => void;
  canWrite?: boolean;
  /**
   * Generate tab, prompt form, and "Back to prompt". True for strategy slides
   * and the brand library. False when the host is only attaching existing media
   * (concept proposal art, brief shot visuals).
   */
  allowGenerate?: boolean;
}

export function errorMessage(error: unknown): string {
  if (typeof error === "object" && error !== null) {
    const data = (error as { response?: { data?: { error?: string; message?: string } } })
      .response?.data;
    if (data?.error) return data.error;
    if (data?.message) return data.message;
    if (error instanceof Error) return error.message;
  }
  return "Something went wrong. Please try again.";
}

function QuietLink({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[13px] text-co-muted transition-colors hover:text-co-text"
    >
      {children}
    </button>
  );
}

function BackToPrompt({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-3 inline-flex items-center gap-1 text-[13px] text-co-muted transition-colors hover:text-co-text"
    >
      <ChevronLeft className="size-3.5" strokeWidth={2} />
      Back to prompt
    </button>
  );
}

/** The shine badge: what marks an image as AI-generated everywhere it is listed. */
function AiBadge() {
  return (
    <span
      title="Generated with AI"
      className="absolute left-1 top-1 grid size-5 place-items-center bg-co-surface-solid text-co-accent"
    >
      <Sparkles className="size-3" strokeWidth={2} />
    </span>
  );
}

function MediaTile({
  item,
  disabled,
  onPick,
}: {
  item: BrandMediaItem;
  disabled?: boolean;
  onPick: (item: BrandMediaItem) => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onPick(item)}
      className="relative aspect-square overflow-hidden rounded-card text-left outline-offset-0 transition hover:outline-2 hover:outline-co-accent disabled:opacity-60"
    >
      <Image
        src={item.thumbUrl ?? item.url ?? undefined}
        alt={item.fileName}
        className="size-full object-cover"
      />
      {item.isAiGenerated ? <AiBadge /> : null}
    </button>
  );
}

function LibraryGrid({
  media,
  isLoading,
  empty,
  compact,
  disabled,
  onPick,
}: {
  media: BrandMediaItem[];
  isLoading: boolean;
  empty: string;
  compact?: boolean;
  disabled?: boolean;
  onPick: (item: BrandMediaItem) => void;
}) {
  const cols = compact
    ? "grid-cols-4 sm:grid-cols-5"
    : "grid-cols-2 sm:grid-cols-4";
  const count = compact ? STRIP_COUNT : 4;

  if (isLoading) {
    return (
      <div className={cn("grid gap-2", cols)}>
        {Array.from({ length: count }, (_, i) => (
          <div
            key={i}
            className="aspect-square animate-pulse rounded-card bg-co-surface"
          />
        ))}
      </div>
    );
  }

  if (media.length === 0) {
    return <p className="py-6 text-center text-[13px] text-co-muted">{empty}</p>;
  }

  return (
    <div className={cn("grid gap-2", cols)}>
      {media.map((item) => (
        <MediaTile key={item.id} item={item} disabled={disabled} onPick={onPick} />
      ))}
    </div>
  );
}

export function MediaPickerBody({
  brandId,
  isOpen,
  tab,
  onTabChange,
  mode,
  defaultAspect = "1:1",
  initialPrompt,
  initialPreviewId,
  onSelect,
  onClose,
  canWrite = true,
  allowGenerate = true,
  createTarget,
  hideLibrary = false,
}: MediaPickerBodyProps) {
  const [prompt, setPrompt] = useState(initialPrompt ?? "");
  const [shape, setShape] = useState<ShapeChoice>(
    defaultAspect === "4:5" ? "custom" : defaultAspect,
  );
  const [resolution, setResolution] = useState<BrandMediaResolution>("1K");
  const [customWidth, setCustomWidth] = useState("1024");
  const [customHeight, setCustomHeight] = useState(defaultAspect === "4:5" ? "1280" : "1024");
  const [error, setError] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(initialPreviewId ?? null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // hideLibrary (a guest): the library query never fires, so every library
  // strip reads as empty and only upload/generate remain.
  const { data, isLoading } = useBrandMedia(isOpen && !hideLibrary ? brandId : null);
  const library = useBrandMediaMutations(brandId);
  const { remove } = library;
  const upload = createTarget?.upload ?? library.upload;
  const generate = createTarget?.generate ?? library.generate;

  const media = useMemo(() => data?.media ?? [], [data]);
  const strip = useMemo(() => media.slice(0, STRIP_COUNT), [media]);
  // Derived, not held: a deleted item drops out of the list and takes its own
  // detail panel with it, with no second piece of state to keep in step.
  const preview = useMemo(
    () => media.find((item) => item.id === previewId) ?? null,
    [media, previewId],
  );
  const busy = upload.isPending || generate.isPending;

  const handleClose = useCallback(() => {
    setError(null);
    setPreviewId(null);
    setPrompt(initialPrompt ?? "");
    onClose();
  }, [onClose, initialPrompt]);

  const pick = useCallback(
    (item: BrandMediaItem) => {
      if (mode === "select") {
        onSelect?.(item, "library");
        handleClose();
        return;
      }
      setPreviewId(item.id);
    },
    [mode, onSelect, handleClose],
  );

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      try {
        const created = await upload.mutateAsync(file);
        if (mode === "select") {
          onSelect?.(created, "created");
          handleClose();
        } else {
          setPreviewId(created.id);
          onTabChange(allowGenerate ? "generate" : "library");
        }
      } catch (err) {
        setError(errorMessage(err));
      }
    },
    [upload, mode, onSelect, handleClose, onTabChange, allowGenerate],
  );

  // What the current settings will actually produce, after the backend snaps
  // edges to its 16px grid. Null means the custom size is unusable as typed.
  const outputSize = useMemo(() => {
    if (shape !== "custom") return generationPresetSize(shape, resolution);
    const w = Number(customWidth);
    const h = Number(customHeight);
    if (!Number.isFinite(w) || w <= 0 || !Number.isFinite(h) || h <= 0) return null;
    const width = snapGenerationEdge(w);
    const height = snapGenerationEdge(h);
    if (Math.max(width, height) / Math.min(width, height) > GENERATION_MAX_RATIO) return null;
    return { width, height };
  }, [shape, resolution, customWidth, customHeight]);

  const handleGenerate = useCallback(async () => {
    const trimmed = prompt.trim();
    if (!trimmed || !outputSize) return;
    setError(null);
    try {
      const input: BrandMediaGenerateInput =
        shape === "custom"
          ? { prompt: trimmed, width: outputSize.width, height: outputSize.height }
          : { prompt: trimmed, aspectRatio: shape, resolution };
      const created = await generate.mutateAsync(input);
      setPrompt("");
      if (mode === "select") {
        onSelect?.(created, "created");
        handleClose();
      } else {
        setPreviewId(created.id);
        onTabChange("generate");
      }
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [prompt, shape, resolution, outputSize, generate, mode, onSelect, handleClose, onTabChange]);

  const handleDelete = useCallback(
    async (mediaId: string) => {
      setError(null);
      try {
        await remove.mutateAsync(mediaId);
        setPreviewId(null);
      } catch (err) {
        setError(errorMessage(err));
      }
    },
    [remove],
  );

  const activeTab: MediaPickerTab = !canWrite
    ? "library"
    : tab === "generate" && !allowGenerate
      ? "library"
      : tab;
  const showBack = canWrite && allowGenerate && activeTab !== "generate";
  const showSourceSwitch = canWrite && !allowGenerate;

  return (
    <>
      {error ? (
        <p className="mb-3 rounded-card bg-co-danger/10 px-3 py-2 text-[12px] text-co-alert-ink">
          {error}
        </p>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {showBack ? <BackToPrompt onClick={() => onTabChange("generate")} /> : null}

        {showSourceSwitch ? (
          <KitSegmentedControl
            options={[
              { label: "Library", value: "library" },
              { label: "Upload", value: "upload" },
            ]}
            value={activeTab === "upload" ? "upload" : "library"}
            onChange={onTabChange}
            ariaLabel="Choose from library or upload"
            className="mb-4 w-[220px]"
          />
        ) : null}

        {activeTab === "generate" && canWrite && allowGenerate ? (
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-4">
              <KitTextarea
                label="Prompt"
                rows={4}
                maxLength={2000}
                value={prompt}
                disabled={generate.isPending}
                placeholder="A sunlit ceramic studio, warm neutral tones, shallow depth of field…"
                onChange={(e) => setPrompt(e.target.value)}
              />
              <div className="flex flex-wrap items-end gap-3">
                <KitSelect
                  label="Shape"
                  value={shape}
                  onChange={(key) => setShape(key as ShapeChoice)}
                  isDisabled={generate.isPending}
                  items={SHAPE_OPTIONS.map((o) => ({ id: o.value, label: o.label }))}
                  className="w-[200px]"
                >
                  {(item: { id: string; label: string }) => (
                    <KitSelectItem key={item.id} id={item.id} textValue={item.label}>
                      {item.label}
                    </KitSelectItem>
                  )}
                </KitSelect>
                {shape !== "custom" ? (
                  <div className="flex flex-col gap-2">
                    <span className="ps-1 text-[11px] font-medium uppercase tracking-[0.14em] text-co-muted">
                      Resolution
                    </span>
                    <KitSegmentedControl
                      options={RESOLUTION_OPTIONS}
                      value={resolution}
                      onChange={setResolution}
                      ariaLabel="Resolution"
                    />
                  </div>
                ) : (
                  <>
                    <KitInput
                      label="Width"
                      type="number"
                      min={GENERATION_MIN_EDGE}
                      max={GENERATION_MAX_EDGE}
                      value={customWidth}
                      disabled={generate.isPending}
                      onChange={(e) => setCustomWidth(e.target.value)}
                      wrapperClassName="w-[110px]"
                    />
                    <KitInput
                      label="Height"
                      type="number"
                      min={GENERATION_MIN_EDGE}
                      max={GENERATION_MAX_EDGE}
                      value={customHeight}
                      disabled={generate.isPending}
                      onChange={(e) => setCustomHeight(e.target.value)}
                      wrapperClassName="w-[110px]"
                    />
                  </>
                )}
              </div>
              <p className="text-[12px] text-co-muted">
                {outputSize
                  ? `Output: ${outputSize.width}×${outputSize.height} px`
                  : `Enter a width and height of ${GENERATION_MIN_EDGE}–${GENERATION_MAX_EDGE} px, no more elongated than ${GENERATION_MAX_RATIO}:1.`}
                {shape === "custom" && outputSize
                  ? " Sizes snap to multiples of 16."
                  : null}
              </p>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[12px] text-co-muted">
                  {generate.isPending
                    ? "Generating — this usually takes about 30 seconds."
                    : (createTarget?.generateDescription ??
                      "Generated images are saved to the brand's library.")}
                </span>
                <KitButton
                  onClick={() => void handleGenerate()}
                  loading={generate.isPending}
                  isDisabled={!prompt.trim() || !outputSize}
                  iconLeft={Sparkles}
                >
                  Generate
                </KitButton>
              </div>
            </div>

            <div className="border-t border-co-line2 pt-4">
              <div className="mb-2 flex items-baseline justify-between gap-3">
                <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-co-faint">
                  Or use an existing image
                </span>
                <div className="flex gap-3">
                  {media.length > STRIP_COUNT ? (
                    <QuietLink onClick={() => onTabChange("library")}>See all</QuietLink>
                  ) : null}
                  <QuietLink onClick={() => onTabChange("upload")}>Upload</QuietLink>
                </div>
              </div>
              <LibraryGrid
                media={strip}
                isLoading={isLoading}
                empty={hideLibrary ? "Upload an image, or generate one above." : "Nothing in the library yet."}
                compact
                disabled={busy}
                onPick={pick}
              />
            </div>
          </div>
        ) : null}

        {activeTab === "library" ? (
          <LibraryGrid
            media={media}
            isLoading={isLoading}
            empty={
              canWrite
                ? allowGenerate
                  ? "No media yet. Generate an image or upload one to start the library."
                  : "No media yet. Upload an image to start the library."
                : "No media in this brand yet."
            }
            disabled={busy}
            onPick={pick}
          />
        ) : null}

        {activeTab === "upload" && canWrite ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <button
              type="button"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                "flex w-full max-w-[420px] flex-col items-center justify-center gap-2 rounded-panel border border-dashed border-co-line2 px-6 py-10 transition hover:border-co-accent",
                busy && "opacity-60",
              )}
            >
              <ImagePlus className="size-5 text-co-faint" />
              <span className="font-section text-[10px] font-semibold uppercase tracking-[0.14em] text-co-faint">
                {upload.isPending ? "Uploading…" : "Choose an image"}
              </span>
              <span className="text-[12px] text-co-muted">JPEG, PNG, WebP or GIF — up to 15 MB</span>
            </button>
          </div>
        ) : null}
      </div>

      {canWrite ? (
        <input
          ref={fileInputRef}
          type="file"
          accept={BRAND_MEDIA_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) void handleFile(file);
          }}
        />
      ) : null}

      {preview ? (
        <div className="mt-4 flex flex-col gap-3 border-t border-co-line2 pt-4">
          <div className="flex items-start gap-3">
            <div className="relative size-20 shrink-0 overflow-hidden rounded-tip">
              <Image
                src={preview.thumbUrl ?? preview.url ?? undefined}
                alt={preview.fileName}
                className="size-full object-cover"
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] text-co-text">{preview.fileName}</p>
              <p className="text-[12px] text-co-muted">
                {preview.width && preview.height
                  ? `${preview.width}x${preview.height} · `
                  : ""}
                {Math.max(1, Math.round(preview.sizeBytes / 1024))} KB
                {preview.isAiGenerated ? " · Generated with AI" : ""}
              </p>
              {preview.prompt ? (
                <p className="mt-1 line-clamp-2 text-[12px] text-co-muted">{preview.prompt}</p>
              ) : null}
            </div>
            {canWrite ? (
              <KitButton
                variant="danger"
                iconLeft={Trash2}
                loading={remove.isPending}
                onClick={() => void handleDelete(preview.id)}
                className="h-9 px-3 text-[13px]"
              >
                Delete
              </KitButton>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}

export default MediaPickerBody;
